import type { MidiNote } from "./music.ts";

export type RhythmRefineStrength = "SOFT" | "NORMAL" | "TIGHT";

export type RhythmRefineOptions = {
  startBar: number;
  barCount: 1 | 2;
  beatsPerBar: number;
  ppq?: number;
  strength?: RhythmRefineStrength;
};

export type RhythmPatternCandidate = {
  gridBeats: number;
  gridLabel: "1/4" | "1/8" | "1/16" | "1/32";
  confidence: number;
  repeatedInterval: boolean;
  repeatedAcrossBars: boolean;
  patternGroups: number[][];
};

export type LocalRhythmRefineResult = {
  range: { startBeat: number; endBeat: number; startBar: number; barCount: number };
  candidate: RhythmPatternCandidate | null;
  strength: RhythmRefineStrength;
  shouldRefine: boolean;
  hasChanges: boolean;
  onsetAdjustments: number;
  durationAdjustments: number;
  refinedNotes: MidiNote[];
  previewNotes: MidiNote[];
  message: string;
};

const CANDIDATE_GRIDS = [
  { beats: 1, label: "1/4" as const, complexityPenalty: 0 },
  { beats: 0.5, label: "1/8" as const, complexityPenalty: 0.02 },
  { beats: 0.25, label: "1/16" as const, complexityPenalty: 0.055 },
  { beats: 0.125, label: "1/32" as const, complexityPenalty: 0.13 },
];

const STRENGTH_WEIGHT: Record<RhythmRefineStrength, number> = { SOFT: 0.4, NORMAL: 0.72, TIGHT: 1 };

const clamp = (value: number, minimum = 0, maximum = 1) => Math.max(minimum, Math.min(maximum, value));
const mean = (values: number[]) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
const nearestGrid = (beat: number, grid: number) => Math.round(beat / grid) * grid;
const normalizedGridError = (beat: number, grid: number) => Math.abs(nearestGrid(beat, grid) - beat) / grid;

function findPatternGroups(starts: number[], grid: number): number[][] {
  if (starts.length < 3) return [];
  const groups: number[][] = [];
  let current = [0];
  for (let index = 1; index < starts.length; index += 1) {
    const interval = starts[index] - starts[index - 1];
    const units = Math.round(interval / grid);
    const regular = units >= 1 && Math.abs(interval - units * grid) <= grid * 0.24;
    if (regular) current.push(index);
    else {
      if (current.length >= 3) groups.push(current);
      current = [index];
    }
  }
  if (current.length >= 3) groups.push(current);
  return groups;
}

export function analyzeLocalRhythmRange(notes: MidiNote[], options: Omit<RhythmRefineOptions, "barCount"> & { barCount: number }): LocalRhythmRefineResult {
  const barCount = Math.max(1, Math.floor(options.barCount));
  if (barCount <= 2) return analyzeLocalRhythm(notes, { ...options, barCount: barCount as 1 | 2 });
  let refinedNotes = notes.map((note) => ({ ...note }));
  const results: LocalRhythmRefineResult[] = [];
  for (let offset = 0; offset < barCount; offset += 2) {
    const chunkCount = Math.min(2, barCount - offset) as 1 | 2;
    const result = analyzeLocalRhythm(refinedNotes, {
      ...options,
      startBar: options.startBar + offset,
      barCount: chunkCount,
    });
    refinedNotes = result.refinedNotes;
    results.push(result);
  }
  const startBeat = Math.max(0, Math.floor(options.startBar)) * Math.max(1 / 8, options.beatsPerBar);
  const endBeat = startBeat + barCount * Math.max(1 / 8, options.beatsPerBar);
  const candidates = results.flatMap((result) => result.candidate ? [result.candidate] : []);
  const onsetAdjustments = results.reduce((sum, result) => sum + result.onsetAdjustments, 0);
  const durationAdjustments = results.reduce((sum, result) => sum + result.durationAdjustments, 0);
  const shouldRefine = results.some((result) => result.shouldRefine);
  const hasChanges = onsetAdjustments + durationAdjustments > 0;
  return {
    range: { startBeat, endBeat, startBar: Math.max(0, Math.floor(options.startBar)), barCount },
    candidate: candidates.sort((left, right) => right.confidence - left.confidence)[0] ?? null,
    strength: options.strength ?? "NORMAL",
    shouldRefine,
    hasChanges,
    onsetAdjustments,
    durationAdjustments,
    refinedNotes,
    previewNotes: refinedNotes.filter((note) => note.startBeat >= startBeat && note.startBeat < endBeat),
    message: hasChanges
      ? `${barCount}小節を2小節ずつ解析した局所パターン候補です。`
      : `${barCount}小節を2小節ずつ確認しました。変更候補はありません。`,
  };
}

function hasRepeatedBarPattern(starts: number[], startBeat: number, beatsPerBar: number, grid: number): boolean {
  const first = starts.filter((beat) => beat < startBeat + beatsPerBar).map((beat) => Math.round((beat - startBeat) / grid));
  const second = starts.filter((beat) => beat >= startBeat + beatsPerBar).map((beat) => Math.round((beat - startBeat - beatsPerBar) / grid));
  if (first.length < 2 || first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
}

function evaluateCandidate(
  starts: number[],
  rangeStart: number,
  beatsPerBar: number,
  barCount: 1 | 2,
  grid: typeof CANDIDATE_GRIDS[number],
): RhythmPatternCandidate {
  const intervals = starts.slice(1).map((start, index) => start - starts[index]).filter((interval) => interval > 0);
  const onsetFit = 1 - clamp(mean(starts.map((start) => normalizedGridError(start - rangeStart, grid.beats))) / 0.34);
  const intervalFit = intervals.length
    ? 1 - clamp(mean(intervals.map((interval) => normalizedGridError(interval, grid.beats))) / 0.28)
    : 0;
  const offsets = starts.map((start) => nearestGrid(start - rangeStart, grid.beats) - (start - rangeStart));
  const offsetMean = mean(offsets);
  const phaseSpread = Math.sqrt(mean(offsets.map((offset) => (offset - offsetMean) ** 2)));
  const phaseCoherence = 1 - clamp(phaseSpread / (grid.beats * 0.28));
  const intervalBuckets = new Map<number, number>();
  intervals.forEach((interval) => {
    const units = Math.max(1, Math.round(interval / grid.beats));
    intervalBuckets.set(units, (intervalBuckets.get(units) ?? 0) + 1);
  });
  const repetition = intervals.length ? Math.max(...intervalBuckets.values()) / intervals.length : 0;
  const groups = findPatternGroups(starts, grid.beats);
  const repeatedAcrossBars = barCount === 2 && hasRepeatedBarPattern(starts, rangeStart, beatsPerBar, grid.beats);
  const barAnchor = starts.some((start) => Math.abs(start - Math.round(start / beatsPerBar) * beatsPerBar) <= grid.beats * 0.2) ? 1 : 0;
  const confidence = clamp(
    onsetFit * 0.27
      + intervalFit * 0.25
      + phaseCoherence * 0.24
      + repetition * 0.14
      + barAnchor * 0.05
      + (repeatedAcrossBars ? 0.08 : 0)
      + (groups.length ? 0.04 : 0)
      - grid.complexityPenalty,
  );
  return {
    gridBeats: grid.beats,
    gridLabel: grid.label,
    confidence,
    repeatedInterval: repetition >= 0.55 && intervalFit >= 0.65,
    repeatedAcrossBars,
    patternGroups: groups,
  };
}

function candidateTargets(starts: number[], rangeStart: number, candidate: RhythmPatternCandidate): number[] {
  const targets = starts.map((start) => nearestGrid(start - rangeStart, candidate.gridBeats) + rangeStart);
  for (const group of candidate.patternGroups) {
    const firstIndex = group[0];
    const rawAnchor = starts[firstIndex];
    const alignedAnchor = nearestGrid(rawAnchor - rangeStart, candidate.gridBeats) + rangeStart;
    group.forEach((noteIndex) => {
      targets[noteIndex] = alignedAnchor + Math.round((starts[noteIndex] - rawAnchor) / candidate.gridBeats) * candidate.gridBeats;
    });
  }
  return targets;
}

export function analyzeLocalRhythm(notes: MidiNote[], options: RhythmRefineOptions): LocalRhythmRefineResult {
  const ppq = Math.max(24, Math.round(options.ppq ?? 480));
  const strength = options.strength ?? "NORMAL";
  const beatsPerBar = Math.max(1 / 8, options.beatsPerBar);
  const startBar = Math.max(0, Math.floor(options.startBar));
  const barCount = options.barCount === 2 ? 2 : 1;
  const startBeat = startBar * beatsPerBar;
  const endBeat = startBeat + barCount * beatsPerBar;
  const selected = notes
    .map((note, index) => ({ note, index }))
    .filter(({ note }) => note.startBeat >= startBeat && note.startBeat < endBeat)
    .sort((left, right) => left.note.startBeat - right.note.startBeat || left.index - right.index);
  const range: LocalRhythmRefineResult["range"] = { startBeat, endBeat, startBar, barCount };
  const base = {
    range,
    strength,
    refinedNotes: notes.map((note) => ({ ...note })),
  };
  if (selected.length < 2) {
    return { ...base, candidate: null, shouldRefine: false, hasChanges: false, onsetAdjustments: 0, durationAdjustments: 0, previewNotes: [], message: "規則を比べるには、この範囲に2音以上必要です。" };
  }

  const rawStarts = selected.map(({ note }) => (note.rawStartTick ?? Math.round(note.startBeat * ppq)) / ppq);
  const candidates = CANDIDATE_GRIDS.map((grid) => evaluateCandidate(rawStarts, startBeat, beatsPerBar, barCount, grid));
  const candidate = candidates.sort((left, right) => right.confidence - left.confidence || right.gridBeats - left.gridBeats)[0];
  const shouldRefine = candidate.confidence >= 0.68 && (candidate.repeatedInterval || candidate.repeatedAcrossBars || candidate.patternGroups.length > 0);
  if (!shouldRefine) {
    return {
      ...base,
      candidate,
      shouldRefine: false,
      hasChanges: false,
      onsetAdjustments: 0,
      durationAdjustments: 0,
      previewNotes: selected.map(({ note }) => ({ ...note })),
      message: "明確な規則を見つけられませんでした。元のリズムを維持します。",
    };
  }

  const targets = candidateTargets(rawStarts, startBeat, candidate);
  const weight = STRENGTH_WEIGHT[strength];
  let onsetAdjustments = 0;
  let durationAdjustments = 0;
  const previewNotes: MidiNote[] = [];
  selected.forEach(({ note, index }, selectedIndex) => {
    const rawStartTick = note.rawStartTick ?? Math.round(note.startBeat * ppq);
    const rawEndTick = note.rawEndTick ?? Math.max(rawStartTick + 1, Math.round((note.startBeat + note.durationBeats) * ppq));
    const rawStartBeat = rawStartTick / ppq;
    const rawEndBeat = rawEndTick / ppq;
    const targetStart = targets[selectedIndex];
    const refinedStartBeat = rawStartBeat + (targetStart - rawStartBeat) * weight;
    const targetEnd = nearestGrid(rawEndBeat - startBeat, candidate.gridBeats) + startBeat;
    const refinedEndBeat = Math.max(refinedStartBeat + 1 / ppq, rawEndBeat + (targetEnd - rawEndBeat) * weight);
    const refinedStartTick = Math.round(refinedStartBeat * ppq);
    const refinedEndTick = Math.max(refinedStartTick + 1, Math.round(refinedEndBeat * ppq));
    if (Math.abs(refinedStartTick - Math.round(note.startBeat * ppq)) > 1) onsetAdjustments += 1;
    if (Math.abs((refinedEndTick - refinedStartTick) - Math.round(note.durationBeats * ppq)) > 1) durationAdjustments += 1;
    const refined: MidiNote = {
      ...note,
      startBeat: refinedStartTick / ppq,
      durationBeats: (refinedEndTick - refinedStartTick) / ppq,
      rawStartTick,
      rawEndTick,
      quantizedStartTick: note.quantizedStartTick ?? Math.round(note.startBeat * ppq),
      quantizedEndTick: note.quantizedEndTick ?? Math.round((note.startBeat + note.durationBeats) * ppq),
      refinedStartTick,
      refinedEndTick,
      refinement: "LOCAL_PATTERN",
      refinementConfidence: candidate.confidence,
    };
    base.refinedNotes[index] = refined;
    previewNotes.push(refined);
  });
  const hasChanges = onsetAdjustments + durationAdjustments > 0;
  return {
    ...base,
    candidate,
    shouldRefine: true,
    hasChanges,
    onsetAdjustments,
    durationAdjustments,
    previewNotes,
    message: hasChanges
      ? candidate.gridLabel + "の局所パターン候補です。元の音を残したまま比較してください。"
      : "すでに" + candidate.gridLabel + "のパターンへ整っています。",
  };
}
